import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { upsert } from '../../../Redux/studentsSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import { WEEKDAY_KEYS } from '../../../i18n/weekdays';
import { ApiError } from '../../../Models/ApiModel';
import type { StudentModel, StudentSlotModel } from '../../../Models/StudentModel';
import type { Weekday } from '../../../Models/enums';
import { StudentService } from '../../../Services/StudentService';
import { CalendarAutoSync } from '../../../Services/CalendarAutoSync';

const WEEKDAYS: Weekday[] = [0, 1, 2, 3, 4, 5, 6];

/**
 * The student's recurring hours, edited where the rest of their card is.
 *
 * A slot is what lets a lesson entered without a time land at the right hour,
 * and what decides when that lesson stops being scheduled and starts being a
 * debt. Getting to it used to mean a separate screen.
 *
 * Every change is saved on the spot rather than with the rest of the form.
 * Slots have their own endpoints, and holding them until submit would mean
 * inventing a diff for a list the server is already the authority on.
 */
export function SlotEditor({ student }: { student: StudentModel }) {
  const { tr } = useTranslation();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(state => state.session.user?.uid ?? '');
  const [weekday, setWeekday] = useState<Weekday>(0);
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('45');
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);

  function replaceSlots(slots: StudentSlotModel[]) {
    dispatch(upsert({ ...student, slots }));
  }

  async function add() {
    if (!time) return;
    setBusy(true);
    setConflict(false);
    try {
      const slot = await StudentService.addSlot(student.name, {
        weekday,
        time,
        durationMinutes: Number(duration) || 45
      });
      replaceSlots([...student.slots, slot]);
      setTime('');
      // Best effort and not awaited: the slot is saved either way, and the
      // recurring event catches up. The id comes back so a later edit patches
      // that event rather than adding a second weekly series.
      void CalendarAutoSync.slotSaved(student.name, slot, userId).then(eventId => {
        if (eventId) replaceSlots([...student.slots, { ...slot, gcalEventId: eventId }]);
      });
    } catch (failure) {
      // A recurring hour that collides with another student's is the one
      // failure worth naming: it is an expected outcome the tutor has to
      // react to, not a hiccup. Anything else leaves the list as the server
      // still has it, same as before.
      if (failure instanceof ApiError && failure.code === 'SLOT_CONFLICT') setConflict(true);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    setBusy(true);
    const removed = student.slots.find(slot => slot.id === id);
    try {
      await StudentService.removeSlot(id);
      replaceSlots(student.slots.filter(slot => slot.id !== id));
      if (removed) void CalendarAutoSync.slotRemoved(removed);
    } catch {
      // Same: the slot stays listed because it is still stored.
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="editor-section is-wide">
      <h3>{tr('recurringHours')}</h3>

      {student.slots.length > 0 && (
        <ul className="editor-chip-list">
          {student.slots.map(slot => (
            <li key={slot.id}>
              <bdi>{tr(WEEKDAY_KEYS[slot.weekday])} {slot.time} · {slot.durationMinutes}′</bdi>
              <button type="button" onClick={() => void remove(slot.id)} disabled={busy} aria-label={tr('remove')}>×</button>
            </li>
          ))}
        </ul>
      )}

      {conflict && <p className="editor-section-error" role="alert">{tr('slotConflict')}</p>}

      <div className="editor-row">
        <label className="field">
          <span>{tr('weekday')}</span>
          <select
            className="text-input"
            value={weekday}
            onChange={e => { setWeekday(Number(e.target.value) as Weekday); setConflict(false); }}
          >
            {WEEKDAYS.map(day => <option key={day} value={day}>{tr(WEEKDAY_KEYS[day])}</option>)}
          </select>
        </label>
        <label className="field">
          <span>{tr('time')}</span>
          <input
            className="text-input"
            type="time"
            value={time}
            onChange={e => { setTime(e.target.value); setConflict(false); }}
          />
        </label>
        <label className="field">
          <span>{tr('durationMinutes')}</span>
          <input
            className="text-input"
            inputMode="numeric"
            value={duration}
            onChange={e => { setDuration(e.target.value.replace(/\D/g, '')); setConflict(false); }}
          />
        </label>
        {/* type=button, or it submits the form it is nested in. */}
        <button type="button" className="ghost-button" onClick={() => void add()} disabled={busy || !time}>
          {tr('add')}
        </button>
      </div>
    </section>
  );
}
